import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import {
  Heart,
  Share2,
  MessageCircle,
  MapPin,
  Calendar,
  Eye,
  Tag,
  Truck,
  Package,
  ChevronLeft,
  ChevronRight,
  Edit,
  Trash2,
  CheckCircle,
} from 'lucide-react';
import { useTheme } from '../../contexts/ThemeContext';
import { useAuth } from '../../contexts/AuthContext';
import { resolveImageUrl } from '../../profile/hooks/useProfileImage';
import { useStreamChat } from '../../messaging/contexts/StreamChatContext';
import Breadcrumb from '../../components/common/Breadcrumb';
import ShareModal from '../../components/common/ShareModal';
import marketplaceAPI from '../services/marketplaceService';
import toast from 'react-hot-toast';
import { btn } from '../../utils/buttonStyles';

const conditionLabels = {
  new: 'New',
  like_new: 'Like New',
  good: 'Good',
  fair: 'Fair',
  poor: 'Poor',
};

const conditionColors = {
  new: 'bg-green-100 text-green-800',
  like_new: 'bg-blue-100 text-blue-800',
  good: 'bg-yellow-100 text-yellow-800',
  fair: 'bg-orange-100 text-orange-800',
  poor: 'bg-red-100 text-red-800',
};

export default function ListingDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { theme } = useTheme();
  const { user } = useAuth();
  const { createOrGetMarketplaceChannel } = useStreamChat();

  const [listing, setListing] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isSaved, setIsSaved] = useState(false);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showShareModal, setShowShareModal] = useState(false);

  useEffect(() => {
    const loadListing = async () => {
      try {
        const data = await marketplaceAPI.getListingById(parseInt(id));
        if (!data || data.status === 'deleted') {
          toast.error('Listing not found');
          navigate('/marketplace');
          return;
        }
        setListing(data);

        // Check if saved
        if (user) {
          try {
            const savedCheck = await marketplaceAPI.checkIfSaved(parseInt(id));
            setIsSaved(savedCheck.isSaved);
          } catch (e) {
            // Ignore - might not be logged in
          }
        }
      } catch (error) {
        console.error('Failed to load listing:', error);
        toast.error('Failed to load listing');
        navigate('/marketplace');
      } finally {
        setLoading(false);
      }
    };
    loadListing();
  }, [id, navigate, user]);

  const handleSave = async () => {
    if (!user) {
      toast.error('Please sign in to save listings');
      return;
    }

    try {
      if (isSaved) {
        await marketplaceAPI.unsaveListing(listing.id);
        setIsSaved(false);
        toast.success('Removed from saved');
      } else {
        await marketplaceAPI.saveListing(listing.id);
        setIsSaved(true);
        toast.success('Listing saved');
      }
    } catch (error) {
      toast.error('Failed to update saved status');
    }
  };

  const handleShare = () => {
    setShowShareModal(true);
  };

  const handleMessageSeller = async () => {
    if (!user) {
      toast.error('Please sign in to message the seller');
      navigate('/signin');
      return;
    }

    try {
      const primaryImage = listing.images?.[0]?.url || '';
      const result = await createOrGetMarketplaceChannel(listing.seller.id, {
        id: listing.id,
        title: listing.title,
        price: listing.price,
        image: primaryImage,
      });
      navigate(`/messages?tab=marketplace&channel=${result.channelId}`);
    } catch (error) {
      console.error('Failed to create chat:', error);
      toast.error('Failed to start conversation');
    }
  };

  const handleDelete = async () => {
    try {
      await marketplaceAPI.deleteListing(listing.id);
      toast.success('Listing deleted');
      navigate('/marketplace');
    } catch (error) {
      toast.error('Failed to delete listing');
    }
  };

  const handleMarkAsSold = async () => {
    try {
      await marketplaceAPI.markAsSold(listing.id);
      setListing((prev) => ({ ...prev, status: 'sold' }));
      toast.success('Listing marked as sold');
    } catch (error) {
      toast.error('Failed to update listing');
    }
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(price);
  };

  const formatDate = (date) => {
    return new Date(date).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  if (loading) {
    return (
      <div className={`min-h-screen ${theme.background} py-8`}>
        <div className="max-w-4xl mx-auto px-4">
          <div className="animate-pulse">
            <div className="h-8 w-24 bg-gray-200 dark:bg-gray-700 rounded mb-6" />
            <div className="h-96 bg-gray-200 dark:bg-gray-700 rounded-lg mb-6" />
            <div className="h-8 w-3/4 bg-gray-200 dark:bg-gray-700 rounded mb-4" />
            <div className="h-12 w-1/4 bg-gray-200 dark:bg-gray-700 rounded mb-6" />
            <div className="h-32 bg-gray-200 dark:bg-gray-700 rounded" />
          </div>
        </div>
      </div>
    );
  }

  if (!listing) {
    return null;
  }

  const images = listing.images || [];
  const isOwner = user?.id === listing.seller?.id;

  return (
    <div className={`min-h-screen ${theme.background}`}>
      <div className="max-w-4xl mx-auto px-4 py-6">
        {/* Breadcrumb */}
        <div className="mb-6">
          <Breadcrumb
            items={[
              { label: 'Home', path: '/' },
              { label: 'Marketplace', path: '/marketplace' },
              { label: listing.title || 'Listing' }
            ]}
          />
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
          {/* Left Column - Images */}
          <div className="lg:col-span-3">
            {/* Main Image */}
            <div className={`relative rounded-lg overflow-hidden ${theme.backgroundSecondary} aspect-square`}>
              {listing.status === 'sold' && (
                <div className="absolute inset-0 bg-black/50 flex items-center justify-center z-10">
                  <span className="text-white text-4xl font-bold">SOLD</span>
                </div>
              )}
              {images.length > 0 ? (
                <img
                  src={resolveImageUrl(images[currentImageIndex].url)}
                  alt={listing.title}
                  className="w-full h-full object-contain"
                />
              ) : (
                <div className={`w-full h-full flex items-center justify-center ${theme.textMuted}`}>
                  No Image
                </div>
              )}

              {images.length > 1 && (
                <>
                  <button
                    onClick={() =>
                      setCurrentImageIndex((prev) =>
                        prev === 0 ? images.length - 1 : prev - 1
                      )
                    }
                    className="absolute left-2 top-1/2 -translate-y-1/2 p-2 rounded-full bg-white/80 hover:bg-white shadow"
                  >
                    <ChevronLeft className="w-6 h-6" />
                  </button>
                  <button
                    onClick={() =>
                      setCurrentImageIndex((prev) =>
                        prev === images.length - 1 ? 0 : prev + 1
                      )
                    }
                    className="absolute right-2 top-1/2 -translate-y-1/2 p-2 rounded-full bg-white/80 hover:bg-white shadow"
                  >
                    <ChevronRight className="w-6 h-6" />
                  </button>
                </>
              )}
            </div>

            {/* Thumbnails */}
            {images.length > 1 && (
              <div className="flex gap-2 mt-4 overflow-x-auto pb-2">
                {images.map((img, index) => (
                  <button
                    key={img.id}
                    onClick={() => setCurrentImageIndex(index)}
                    className={`flex-shrink-0 w-20 h-20 rounded-lg overflow-hidden border-2 ${
                      index === currentImageIndex
                        ? 'border-blue-500'
                        : 'border-transparent'
                    }`}
                  >
                    <img
                      src={resolveImageUrl(img.url)}
                      alt={`${listing.title} ${index + 1}`}
                      className="w-full h-full object-cover"
                    />
                  </button>
                ))}
              </div>
            )}

            {/* Description */}
            <div className={`mt-6 ${theme.card} rounded-lg p-6`}>
              <h3 className={`text-lg font-semibold mb-3 ${theme.text}`}>
                Description
              </h3>
              <p className={`${theme.textSecondary} whitespace-pre-wrap`}>
                {listing.description || 'No description provided.'}
              </p>
            </div>
          </div>

          {/* Right Column - Details */}
          <div className="lg:col-span-2">
            <div className={`${theme.card} rounded-lg p-6 sticky top-6`}>
              {/* Title & Price */}
              <h1 className={`text-2xl font-bold mb-2 ${theme.text}`}>
                {listing.title}
              </h1>
              <p className={`text-3xl font-bold ${theme.link} mb-4`}>
                {formatPrice(listing.price)}
                {listing.isNegotiable && (
                  <span className={`ml-2 text-sm font-normal ${theme.textSecondary}`}>
                    OBO
                  </span>
                )}
              </p>

              {/* Badges */}
              <div className="flex flex-wrap gap-2 mb-4">
                <span
                  className={`px-3 py-1 text-sm font-medium rounded-full ${
                    conditionColors[listing.condition]
                  }`}
                >
                  {conditionLabels[listing.condition]}
                </span>
                {listing.category && (
                  <span className={`px-3 py-1 text-sm font-medium ${theme.backgroundSecondary} ${theme.text} rounded-full flex items-center gap-1`}>
                    <Tag className="w-4 h-4" />
                    {listing.category.categoryName}
                  </span>
                )}
              </div>

              {/* Details List */}
              <div className="space-y-3 mb-6">
                {listing.brand && (
                  <div className={`flex items-center gap-2 ${theme.textSecondary}`}>
                    <Package className="w-4 h-4" />
                    <span>Brand: {listing.brand}</span>
                    {listing.model && <span>• {listing.model}</span>}
                  </div>
                )}
                {listing.yearPurchased && (
                  <div className={`flex items-center gap-2 ${theme.textSecondary}`}>
                    <Calendar className="w-4 h-4" />
                    <span>Purchased: {listing.yearPurchased}</span>
                  </div>
                )}
                {listing.locationText && (
                  <div className={`flex items-center gap-2 ${theme.textSecondary}`}>
                    <MapPin className="w-4 h-4" />
                    <span>{listing.locationText}</span>
                  </div>
                )}
                <div className={`flex items-center gap-2 ${theme.textSecondary}`}>
                  <Truck className="w-4 h-4" />
                  <span>
                    {listing.shippingAvailable
                      ? 'Shipping available'
                      : 'Pickup only'}
                  </span>
                </div>
                <div className={`flex items-center gap-2 ${theme.textSecondary}`}>
                  <Eye className="w-4 h-4" />
                  <span>{listing.viewCount} views</span>
                </div>
                <div className={`flex items-center gap-2 ${theme.textSecondary}`}>
                  <Calendar className="w-4 h-4" />
                  <span>Posted {formatDate(listing.createdAt)}</span>
                </div>
              </div>

              {/* Action Buttons */}
              {isOwner ? (
                // Owner actions
                <div className="space-y-3">
                  {listing.status === 'active' && (
                    <>
                      <Link
                        to={`/marketplace/listing/${listing.id}/edit`}
                        className={`w-full flex items-center justify-center gap-2 px-4 py-3 border ${theme.border} rounded-lg ${theme.text} ${theme.cardHover}`}
                      >
                        <Edit className="w-5 h-5" />
                        Edit Listing
                      </Link>
                      <button
                        onClick={handleMarkAsSold}
                        className={`w-full flex items-center justify-center gap-2 ${btn.base} ${btn.lg} ${theme.buttonPrimary}`}
                      >
                        <CheckCircle className="w-5 h-5" />
                        Mark as Sold
                      </button>
                    </>
                  )}
                  <button
                    onClick={handleShare}
                    className={`w-full flex items-center justify-center gap-2 px-4 py-3 border ${theme.border} rounded-lg ${theme.text} ${theme.cardHover}`}
                  >
                    <Share2 className="w-5 h-5" />
                    Share
                  </button>
                  <button
                    onClick={() => setShowDeleteConfirm(true)}
                    className={`w-full flex items-center justify-center gap-2 ${btn.base} ${btn.lg} ${theme.buttonDanger}`}
                  >
                    <Trash2 className="w-5 h-5" />
                    Delete Listing
                  </button>
                </div>
              ) : (
                // Buyer actions
                <div className="space-y-3">
                  {listing.status === 'active' && (
                    <button
                      onClick={handleMessageSeller}
                      className={`w-full flex items-center justify-center gap-2 ${btn.base} ${btn.lg} ${theme.buttonPrimary}`}
                    >
                      <MessageCircle className="w-5 h-5" />
                      Message Seller
                    </button>
                  )}
                  <div className="flex gap-2">
                    <button
                      onClick={handleSave}
                      className={`flex-1 flex items-center justify-center gap-2 px-4 py-3 border ${theme.border} rounded-lg ${theme.text} ${theme.cardHover}`}
                    >
                      <Heart
                        className={`w-5 h-5 ${
                          isSaved ? 'fill-red-500 text-red-500' : ''
                        }`}
                      />
                      {isSaved ? 'Saved' : 'Save'}
                    </button>
                    <button
                      onClick={handleShare}
                      className={`flex-1 flex items-center justify-center gap-2 px-4 py-3 border ${theme.border} rounded-lg ${theme.text} ${theme.cardHover}`}
                    >
                      <Share2 className="w-5 h-5" />
                      Share
                    </button>
                  </div>
                </div>
              )}

              {/* Seller Info */}
              {listing.seller && !isOwner && (
                <div className={`mt-6 pt-6 border-t ${theme.border}`}>
                  <h3 className={`text-sm font-medium mb-3 ${theme.textSecondary}`}>
                    Seller
                  </h3>
                  <Link
                    to={`/player/${listing.seller.userHandle || listing.seller.id}`}
                    className="flex items-center gap-3 hover:opacity-80"
                  >
                    <div className={`w-12 h-12 rounded-full ${theme.gradient} flex items-center justify-center`}>
                      <span className="text-lg font-bold text-white">
                        {listing.seller.firstName?.[0]?.toUpperCase() || 'U'}
                      </span>
                    </div>
                    <div>
                      <p className={`font-medium ${theme.text}`}>
                        {listing.seller.firstName} {listing.seller.lastName}
                      </p>
                      <p className={`text-sm ${theme.textSecondary}`}>
                        View Profile
                      </p>
                    </div>
                  </Link>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className={`${theme.card} rounded-lg p-6 max-w-sm w-full`}>
            <h3 className={`text-lg font-bold mb-2 ${theme.text}`}>
              Delete Listing?
            </h3>
            <p className={`mb-6 ${theme.textSecondary}`}>
              This action cannot be undone. Are you sure you want to delete this
              listing?
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => setShowDeleteConfirm(false)}
                className={`flex-1 ${btn.base} ${btn.md} ${theme.buttonGhost}`}
              >
                Cancel
              </button>
              <button
                onClick={handleDelete}
                className={`flex-1 ${btn.base} ${btn.md} ${theme.buttonDanger}`}
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Share Modal */}
      <ShareModal
        isOpen={showShareModal}
        onClose={() => setShowShareModal(false)}
        entity={{ id: listing?.id, name: listing?.title }}
        entityType="listing"
        urlPattern="/marketplace/{id}"
        currentUser={user}
      />
    </div>
  );
}
